import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { TimezoneSelect } from "@/components/ui/timezone-select";

export type LocationPoint = {
  latitude: number;
  longitude: number;
  recorded_at: string;
};

type HourlyBucket = {
  hourStart: Dayjs;
  distanceKm: number;
};

function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function buildHourlyBuckets(
  locations: LocationPoint[],
  timezone: string,
): HourlyBucket[] {
  if (locations.length < 2) return [];

  const sorted = [...locations].sort((a, b) =>
    a.recorded_at.localeCompare(b.recorded_at),
  );

  const bucketMap = new Map<string, HourlyBucket>();

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const dist = haversineDistance(
      prev.latitude,
      prev.longitude,
      curr.latitude,
      curr.longitude,
    );

    const hourStart = dayjs.utc(curr.recorded_at).tz(timezone).startOf("hour");
    const key = hourStart.toISOString();

    const existing = bucketMap.get(key);
    if (existing) {
      existing.distanceKm += dist;
    } else {
      bucketMap.set(key, { hourStart, distanceKm: dist });
    }
  }

  return Array.from(bucketMap.values())
    .filter((b) => b.distanceKm >= 0.01)
    .sort((a, b) => a.hourStart.valueOf() - b.hourStart.valueOf());
}

type DayGroup = {
  date: Dayjs;
  buckets: HourlyBucket[];
};

function groupByDay(buckets: HourlyBucket[]): DayGroup[] {
  const groups: DayGroup[] = [];

  for (const bucket of buckets) {
    const last = groups[groups.length - 1];
    if (last && last.date.isSame(bucket.hourStart, "day")) {
      last.buckets.push(bucket);
    } else {
      groups.push({ date: bucket.hourStart, buckets: [bucket] });
    }
  }

  return groups;
}

export function HourlyDistanceTable({
  locations,
}: {
  locations: LocationPoint[];
}) {
  const [timezone, setTimezone] = useState(() => dayjs.tz.guess());

  const buckets = useMemo(
    () => buildHourlyBuckets(locations, timezone),
    [locations, timezone],
  );
  const dayGroups = useMemo(() => groupByDay(buckets), [buckets]);
  const totalDistance = buckets.reduce((sum, b) => sum + b.distanceKm, 0);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <h4 className="text-sm font-medium">Hourly Distance</h4>
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs text-zinc-500">Times in</span>
          <TimezoneSelect
            value={timezone}
            onChange={setTimezone}
            className="!w-auto min-w-[200px]"
          />
        </div>
      </div>
      {buckets.length === 0 ? (
        <div className="rounded-lg border p-6 text-center">
          <p className="text-sm text-zinc-500">
            Not enough location data to calculate hourly distances.
          </p>
        </div>
      ) : (
        <>
          <div className="px-3 pb-2 text-right text-xs text-zinc-500">
            Total: {formatDistance(totalDistance)}
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <Table>
              <TableBody>
                {dayGroups.map((group) => (
                  <>
                    <DayDividerRow
                      key={`divider-${group.date.toISOString()}`}
                      date={group.date}
                      totalKm={group.buckets.reduce(
                        (sum, b) => sum + b.distanceKm,
                        0,
                      )}
                    />
                    {group.buckets.map((bucket, bucketIndex) => (
                      <TableRow
                        key={bucket.hourStart.toISOString()}
                        className={
                          bucketIndex === group.buckets.length - 1
                            ? "border-b-0"
                            : ""
                        }
                      >
                        <TableCell className="px-3 py-1.5 text-sm text-zinc-600">
                          {bucket.hourStart.format("h:mma")}
                        </TableCell>
                        <TableCell className="px-3 py-1.5 text-right font-mono text-sm text-zinc-600">
                          {formatDistance(bucket.distanceKm)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function DayDividerRow({ date, totalKm }: { date: Dayjs; totalKm: number }) {
  return (
    <tr>
      <td colSpan={2} className="px-3 py-1">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs font-medium text-zinc-400">
            {date.format("dddd, D MMMM YYYY")}
          </span>
          <div className="h-px flex-1 bg-zinc-300" />
          <span className="shrink-0 text-xs font-medium text-zinc-500">
            {formatDistance(totalKm)}
          </span>
        </div>
      </td>
    </tr>
  );
}

function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
}
